import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { environment } from '@env/insitestore360';
import { USER_URL } from '@insite-store360/shared';
import { Observable } from 'rxjs';
import { filter } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class LogPageVisitService {
  private apiUrl = environment.apiUrl;

  // Map of routes to page names for Trade Operations sub-menus
  private tradeOpsRoutes: { [key: string]: { page: string; defaultTab?: string } } = {
    // Dashboards
    '/admin/landing-page': { page: 'Landing Page' },
    '/admin/dashboard/Power-BI-Dashboard': { page: 'Power BI Dashboard' },
    '/admin/admin-sales-dashboard/sellout-dashboard': { page: 'Admin Sellout Dashboard', defaultTab: 'Dashboard' },
    '/admin/shop-profiling/dashboard': { page: 'Shop Profiling', defaultTab: 'Dashboard' },
    '/admin/shop-profiling/map-intelligence': { page: 'Shop Profiling', defaultTab: 'Map Intelligence' },
    '/admin/shop-profiling/stores': { page: 'Shop Profiling', defaultTab: 'Stores' },
    '/admin/shop-profiling/raw-data': { page: 'Shop Profiling', defaultTab: 'Raw Data' },
    '/admin/jbp/score-list': { page: 'Customer value assessment', defaultTab: 'Overview' },
    '/admin/jbp/contract-repo': { page: 'Contract Repository', defaultTab: 'Details' },

    // Trade Operations
    '/admin/survey/displaymatrix': { page: 'Display Compliance', defaultTab: 'Details' },
    '/admin/survey/display-compliance': { page: 'Display Compliance v2', defaultTab: 'Details' },
    '/admin/user/display-share': { page: 'Display Share', defaultTab: 'Details' },
    '/admin/user/display-planogram': { page: 'Fixture Share', defaultTab: 'Details' },
    '/admin/survey/stock-tracker': { page: 'Stock' },
    '/admin/survey/market-activity': { page: 'Market Activity' },
    '/admin/survey/snagreport': { page: 'Snag Report' },
    '/admin/journey/journey-dashboard': { page: 'Journey Dashboard', defaultTab: 'Summary' },
    '/admin/user/checkIn-checkOut': { page: 'Store Visits & Attendance', defaultTab: 'Summary' },
    '/admin/user/user-attendance-calendar': { page: 'User Attendance Calendar' },
    '/admin/leave-management/all-request': { page: 'Leave Request' },
    '/admin/user/sellout': { page: 'Promoters Sellout', defaultTab: 'Details' },
    '/admin/user/sellout-daily-target': { page: 'Sellout Daily Target' },
    '/admin/survey/promotershare': { page: 'Promoter Share', defaultTab: 'Details' },
    '/admin/product/incentive/pmi-incentive-report': { page: 'PMI Incentive Report', defaultTab: 'Summary' },
    '/admin/product/incentive/monthly-incentive-report': { page: 'Monthly Incentive Report', defaultTab: 'Summary' },
    '/admin/product/incentive/egypt-incentive-report': { page: 'Egypt Incentive Report', defaultTab: 'Summary' },
    '/admin/training/training-summary': { page: 'Group Training', defaultTab: 'Trainings List' },
    '/admin/training/training-dashboard': { page: 'Training dashboard', defaultTab: 'Dashboard' },
    '/admin/training/group-training-details': { page: 'Group Training Details', defaultTab: 'Summary' },
    '/admin/training/field-training': { page: 'Field Training', defaultTab: 'Summary' },
    '/admin/training/field-training-details': { page: 'Field Training Details', defaultTab: 'Summary' },
    '/admin/training/training-quiz': { page: 'Quiz Bank' },
    '/admin/training/schedule-quiz': { page: 'Quiz List', defaultTab: 'Quiz List' },
    '/admin/training/quiz-details': { page: 'Quiz Results' },
    '/admin/training/quiz-attendees-details': { page: 'Quiz Attendees Details', defaultTab: 'Summary' },
    '/admin/survey/price-tracking': { page: 'Price Tracking' },
    '/admin/product/price-tracking-dash': { page: 'Price Tracking Dashboard', defaultTab: 'Dashboard' },
    '/admin/survey/weekly-price-mapping': { page: 'Weekly Price Mapping' },
    '/admin/survey/price-promotion': { page: 'Price Promotion' },
    '/admin/product/products': { page: 'Products' },
    '/admin/survey/aeg-survey': { page: 'AEG 90cm(FSC)', defaultTab: 'Summary' },
    '/admin/survey/not-moving': { page: 'Non Moving Stock' },
    '/admin/survey/stock-audit': { page: 'Stock Audit' },
    '/admin/survey/store-mapping': { page: 'Store Mapping' },
    '/admin/survey/product-mapping': { page: 'Product Mapping' },
    '/admin/store360': { page: 'Store 360' },
    '/admin/survey/usage-summary': { page: 'Usage Summary' },

    // Setups & Controls
    '/admin/store/store-list': { page: 'Store Setup' },
    '/admin/store/channel': { page: 'Channel Setup' },
    '/admin/store/account': { page: 'Account Setup' },
    '/admin/user/area-mapping': { page: 'Area Mapping Setup' },
    '/admin/user/master-country': { page: 'Country Setup' },
    '/admin/user/master-area': { page: 'Region Setup' },
    '/admin/product/product-line': { page: 'Product Setup' },
    '/admin/product/sync-sap': { page: 'SAP Sync' },
    '/admin/product/sync-retailer': { page: 'Retailer Sync' },
    '/admin/survey/product-rrprice': { page: 'Product Price Setup' },
    '/admin/product/product-grossprice': { page: 'Gross Price Setup' },
    '/admin/product/bundle-offer': { page: 'Bundle Offer Setup' },
    '/admin/product/multi-brand-mapping': { page: 'Multi Brand Mapping' },
    '/admin/product/brand': { page: 'Brand Setup' },
    '/admin/product/product-list': { page: 'Product Line Setup' },
    '/admin/product/category': { page: 'Category Setup' },
    '/admin/product/sub-category': { page: 'Sub Category Setup' },
    '/admin/product/segment-list': { page: 'Segment Setup' },
    '/admin/product/project-name': { page: 'Project Name Setup' },
    '/admin/product/series': { page: 'Series Setup' },
    '/admin/product/mda-sda': { page: 'MDA/SDA Setup' },
    '/admin/product/fs-bi': { page: 'FS/BI Setup' },
    '/admin/user/master-user': { page: 'User Setup' },
    '/admin/user/users': { page: 'User List' },
    '/admin/user/users/add': { page: 'Add User' },
    '/admin/store/user-store-mapping': { page: 'User Store Mapping Setup' },
    '/admin/journey/journey-list': { page: 'Journey Plan Setup' },
    '/admin/user/reporting-manager-mapping': { page: 'Reporting Manager Setup' },
    '/admin/user/tds-view': { page: 'TDS View', defaultTab: 'Summary' },
    '/admin/user/page-visit-logs': { page: 'User Access Logs' },
    '/admin/user/exec-dashboard-upload': { page: 'Exec Dashboard Upload' },
    '/admin/user/monthly-setup': { page: 'Monthly Setup' },
    '/admin/survey/board-paper': { page: 'Board Paper' },
    '/admin/survey/executive-dashboard': { page: 'Executive Dashboard' },
    '/admin/dashboard/brand-visibility': { page: 'Brand Visibility' },
    '/admin/competitive-intelligence': { page: 'Competitive Intelligence' },
    '/admin/product/core-range': { page: 'Core Range Setup' },
    '/admin/user/price-mapping': { page: 'Core Range Product Mapping' },
    '/admin/product/incentive-scheme-types': { page: 'Scheme Types Setup' },
    '/admin/product/incentive-setup': { page: 'Incentive Setup' },
    '/admin/product/incentive-model': { page: 'Per Model Incentive Setup' },
    '/admin/setting/target-setting': { page: 'Promoter Target Setup' },
    '/admin/product/incentive/monthly-target': { page: 'Promoter Target By Category Setup' },
    '/admin/product/tier': { page: 'Tier Setup' },
    '/admin/jbp/factor-setup': { page: 'Factor Setup' },
    '/admin/jbp/sales-contribution-setup': { page: 'Sales Contribution Setup' },
    '/admin/jbp/state-wise-factor-scoring-setup': { page: 'State-wise Scoring Setup' },
    '/leave-management': { page: 'Leave Management' },
    '/admin/leave-management/leave-type': { page: 'Leave Type Setup' },
    '/admin/leave-management/assign-leave': { page: 'Assign Leave Setup' },
    '/admin/activity-calendar/setup/quiz-mandatory': { page: 'Quiz Mandatory' },
    '/admin/training/training-type-setup': { page: 'Training Type Setup' },
    '/admin/training/training-module-setup': { page: 'Training Module Setup' },
    '/admin/training/training-duration-setup': { page: 'Training Duration Setup' },
  };

  constructor(private http: HttpClient, private router: Router) {
    this.initListener();
  }

  private initListener(): void {
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        const url = event.urlAfterRedirects.split('?')[0]; // Remove query params
        const routeConfig = this.tradeOpsRoutes[url];

        if (routeConfig) {
          this.logVisit(routeConfig.page, routeConfig.defaultTab);
        }
      });
  }

  public logVisit(pageName: string, subPageName?: string, countryId?: number | null): void {
    const loginUser = localStorage.getItem('roleDetails');
    let resolvedCountryId = countryId;
    if (!resolvedCountryId && loginUser) {
      try {
        const parsed = JSON.parse(loginUser);
        if (parsed.countryId) {
          resolvedCountryId = parsed.countryId;
        } else if (parsed.country_id) {
          resolvedCountryId = parsed.country_id;
        } else if (parsed.location && parsed.location.countryId) {
          resolvedCountryId = parsed.location.countryId;
        }
      } catch (e) {
        // Ignore JSON parse errors
      }
    }

    const payload = {
      pageName: pageName,
      subPageName: subPageName,
      countryId: resolvedCountryId || null
    };

    this.http.post(`${USER_URL}/logPageVisit/save`, payload).subscribe({
      next: () => console.log(`Logged visit to ${pageName}`),
      error: (err) => console.error('Error logging page visit:', err)
    });
  }

  public getAllLogs(pageIndex: number, pageSize: number, filters?: any): Observable<any> {
    let params = new HttpParams()
      .set('pageIndex', pageIndex.toString())
      .set('pageSize', pageSize.toString());

    if (filters) {
      if (filters.searchFilter) {
        params = params.set('searchFilter', filters.searchFilter);
      }
      if (filters.slNo) {
        params = params.set('slNo', filters.slNo.toString());
      }
      if (filters.userName) {
        params = params.set('userName', filters.userName);
      }
      if (filters.startDate) {
        params = params.set('startDate', filters.startDate);
      }
      if (filters.endDate) {
        params = params.set('endDate', filters.endDate);
      }
      if (filters.countryId) {
        params = params.set('countryId', filters.countryId.toString());
      }
      if (filters.roleName) {
        params = params.set('roleName', filters.roleName);
      }
    }

    return this.http.get<any>(`${USER_URL}/logPageVisit/getAll`, { params });
  }
}
